import { defineMessages, useIntl } from 'react-intl';
import { AccountType, NumberOfEmployees, PaymentMethod } from '../shared/Constants';
import { getNumberOfEmployeesLabel } from '../utils/AppUtil';

const i18nMessages = defineMessages({
  month: {
    id: 'account.subscription.month',
    defaultMessage: 'month',
  },
  months: {
    id: 'account.subscription.months',
    defaultMessage: 'months',
  },
  creditCard: {
    id: 'account.subscription.credit-card',
    defaultMessage: 'Credit card',
  },
  bankTransfer: {
    id: 'account.subscription.bank-transfer',
    defaultMessage: 'Bank transfer',
  },
});

const useSubscription = () => {
  const { formatMessage } = useIntl();

  const buildMethodLabel = (paymentMethod) => {
    switch (paymentMethod) {
      case PaymentMethod.CREDIT_CARD:
        return formatMessage(i18nMessages.creditCard);
      case PaymentMethod.BANK_TRANSFER:
        return formatMessage(i18nMessages.bankTransfer);
      default:
        throw new Error(`Unknown payment method: ${paymentMethod}`);
    }
  };

  const buildSubscriptionPeriodLabel = (subscriptionPeriodDuration) => {
    const timeUnit =
      subscriptionPeriodDuration > 1 ? formatMessage(i18nMessages.months) : formatMessage(i18nMessages.month);

    return `${subscriptionPeriodDuration} ${timeUnit}`;
  };

  const isBankTransferPossible = (
    managingAccountType,
    clientAccountType,
    numberOfEmployees,
    subscriptionPeriodId,
    subscriptionPeriods,
    paymentMethod,
  ) => {
    if (managingAccountType === AccountType.GSC) return false;

    const accountIsNewBusinessModelClient = clientAccountType === AccountType.NBM_CLIENT;

    const minimum1000Employees =
      numberOfEmployees &&
      !(numberOfEmployees === NumberOfEmployees._0_9) &&
      !(numberOfEmployees === NumberOfEmployees._10_24) &&
      !(numberOfEmployees === NumberOfEmployees._25_49) &&
      !(numberOfEmployees === NumberOfEmployees._50_74) &&
      !(numberOfEmployees === NumberOfEmployees._75_99) &&
      !(numberOfEmployees === NumberOfEmployees._100_249) &&
      !(numberOfEmployees === NumberOfEmployees._250_499) &&
      !(numberOfEmployees === NumberOfEmployees._500_999);

    const selectedSubscriptionPeriod = subscriptionPeriods.find(
      (subscriptionPeriod) => subscriptionPeriod.id === subscriptionPeriodId,
    );

    const minimum3Months = selectedSubscriptionPeriod && selectedSubscriptionPeriod.duration >= 3;

    const bankTransferIsPossible = accountIsNewBusinessModelClient && minimum1000Employees && minimum3Months;

    return paymentMethod
      ? paymentMethod === PaymentMethod.BANK_TRANSFER && bankTransferIsPossible
      : bankTransferIsPossible;
  };

  const getPaymentMethodOptions = (
    managingAccountType,
    clientAccountType,
    numberOfEmployees,
    subscriptionPeriodId,
    subscriptionPeriods,
    paymentMethods,
  ) => {
    const bankTransferIsPossible = isBankTransferPossible(
      managingAccountType,
      clientAccountType,
      numberOfEmployees,
      subscriptionPeriodId,
      subscriptionPeriods,
    );

    return paymentMethods
      .filter((entry) => {
        if (bankTransferIsPossible) return true;

        return entry === PaymentMethod.CREDIT_CARD;
      })
      .map((entry) => ({
        value: entry,
        label: buildMethodLabel(entry),
      }));
  };

  const getNumberOfEmployeesOptions = (numbersOfEmployees, clientAccountIsGscOrGp) => {
    return numbersOfEmployees
      .filter((entry) => {
        if (clientAccountIsGscOrGp) {
          return entry === NumberOfEmployees._0_9;
        }

        return entry;
      })
      .map((entry) => ({
        value: entry,
        label: getNumberOfEmployeesLabel(entry),
      }));
  };

  const getSubscriptionPeriodOptions = (subscriptionPeriods) => {
    return subscriptionPeriods.map((entry) => ({
      value: entry.id,
      label: buildSubscriptionPeriodLabel(entry.duration),
    }));
  };

  const buildAccountLabel = (clientAccount) => `${clientAccount.id} - ${clientAccount.displayName}`;

  return {
    isBankTransferPossible,
    getPaymentMethodOptions,
    getNumberOfEmployeesOptions,
    getSubscriptionPeriodOptions,
    buildAccountLabel,
  };
};

export default useSubscription;
